// =====================================================
// app/saved-accounts.tsx
// Écran des comptes mémorisés (façon Netflix / PS4).
// — Affiche les profils sauvegardés (avatar en cache + nom).
// — Tap sur une carte → reconnexion via refresh_token (sans mot de passe).
// — Appui long sur une carte → suppression individuelle.
// — Bouton "Tout effacer" → vide tous les comptes mémorisés.
// — Bouton "Utiliser un autre compte" → renvoie vers /login classique.
// =====================================================

import React, { useState, useCallback } from 'react';
import {
  StyleSheet, Text, View, ScrollView, TouchableOpacity,
  ActivityIndicator, Platform, Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, useFocusEffect } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';

import {
  getSavedAccounts, removeSavedAccount, clearSavedAccounts,
  updateAccountToken, SavedAccount,
} from '../lib/savedAccounts';

const WS_BASE = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const REFRESH_URL = `${WS_BASE}/refresh-token`;
const NATIVE = Platform.OS !== 'web';

const PURPLE = '#7B1FA2';
const PURPLE_DARK = '#4B0082';

function getInitials(nom: string, prenom: string) {
  return `${prenom?.[0] || ''}${nom?.[0] || ''}`.toUpperCase();
}

export default function SavedAccountsScreen() {
  const router = useRouter();
  const { t } = useTranslation();

  const [accounts, setAccounts] = useState<SavedAccount[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [reconnectingId, setReconnectingId] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    setLoadingList(true);
    const list = await getSavedAccounts();
    setAccounts(list);
    setLoadingList(false);
  }, []);

  // Recharge la liste à chaque fois que l'écran reprend le focus
  useFocusEffect(
    useCallback(() => {
      loadAccounts();
    }, [loadAccounts])
  );

  // ── Reconnexion 1-clic via refresh_token ────────────────────────────────
  const handleQuickLogin = async (account: SavedAccount) => {
    if (reconnectingId) return;
    setReconnectingId(account.user_id);
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);

    try {
      const res = await fetch(REFRESH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: account.refresh_token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      await AsyncStorage.setItem('harmonia_session', JSON.stringify({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_at: data.expires_at,
        user: { id: account.user_id },
      }));

      // Rotation du refresh_token côté Supabase : on met à jour la copie
      // mémorisée pour que la prochaine reconnexion rapide fonctionne aussi.
      await updateAccountToken(account.user_id, data.refresh_token);

      // Route canonique après session valide, identique à celle utilisée
      // par LandingPage.tsx lors de la vérification de session existante.
      router.replace('/(tabs)/actu');
    } catch (err) {
      // Le refresh_token a expiré ou a été révoqué : on retire ce profil
      // et on invite l'utilisateur à se reconnecter normalement.
      await removeSavedAccount(account.user_id);
      setAccounts(prev => prev.filter(a => a.user_id !== account.user_id));
    } finally {
      setReconnectingId(null);
    }
  };

  // ── Suppression individuelle (appui long) ───────────────────────────────
  const handleRemoveAccount = async (userId: string) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    await removeSavedAccount(userId);
    setAccounts(prev => prev.filter(a => a.user_id !== userId));
  };

  // ── Tout effacer ─────────────────────────────────────────────────────────
  const handleClearAll = async () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
    await clearSavedAccounts();
    setAccounts([]);
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('savedAccounts.header.title')}</Text>
        <Text style={styles.headerSub}>
          {t('savedAccounts.header.subtitle')}
        </Text>
      </View>

      {loadingList ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={PURPLE} />
        </View>
      ) : accounts.length === 0 ? (
        <View style={styles.centered}>
          <Ionicons name="people-outline" size={56} color="#DDD" />
          <Text style={styles.emptyText}>{t('savedAccounts.empty.title')}</Text>
          <Text style={styles.emptySub}>
            {t('savedAccounts.empty.subtitle')}
          </Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.grid} showsVerticalScrollIndicator={false}>
          {accounts.map(account => (
            <TouchableOpacity
              key={account.user_id}
              style={styles.card}
              activeOpacity={0.85}
              onPress={() => handleQuickLogin(account)}
              onLongPress={() => handleRemoveAccount(account.user_id)}
              disabled={!!reconnectingId}
            >
              <View style={styles.avatarWrapper}>
                {reconnectingId === account.user_id ? (
                  <View style={[styles.avatar, styles.avatarLoading]}>
                    <ActivityIndicator size="small" color={PURPLE} />
                  </View>
                ) : account.avatar_url ? (
                  <Image source={{ uri: account.avatar_url }} style={styles.avatar} resizeMode="cover" />
                ) : (
                  <View style={[styles.avatar, styles.avatarFallback]}>
                    <Text style={styles.avatarFallbackText}>
                      {getInitials(account.nom, account.prenom)}
                    </Text>
                  </View>
                )}
              </View>
              <Text style={styles.cardName} numberOfLines={1}>
                {account.prenom} {account.nom}
              </Text>
              <TouchableOpacity
                style={styles.removeBtn}
                onPress={() => handleRemoveAccount(account.user_id)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="close-circle" size={20} color="#BBB" />
              </TouchableOpacity>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}

      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.otherAccountBtn}
          onPress={() => { if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); router.push('/login'); }}
          activeOpacity={0.8}
        >
          <Ionicons name="person-add-outline" size={18} color={PURPLE} />
          <Text style={styles.otherAccountText}>{t('savedAccounts.useOtherAccount')}</Text>
        </TouchableOpacity>

        {accounts.length > 0 && (
          <TouchableOpacity style={styles.clearAllBtn} onPress={handleClearAll} activeOpacity={0.7}>
            <Text style={styles.clearAllText}>{t('savedAccounts.clearAll')}</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#FAFAFA' },

  header: {
    paddingTop: Platform.OS === 'ios' ? 70 : 50,
    paddingBottom: 20,
    paddingHorizontal: 24,
    alignItems: 'center',
  },
  headerTitle: { fontSize: 24, fontWeight: '800', color: '#1A1A1A', marginBottom: 6 },
  headerSub: { fontSize: 14, color: '#888', textAlign: 'center' },

  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: 8, paddingHorizontal: 40 },
  emptyText: { fontSize: 16, fontWeight: '600', color: '#999', marginTop: 8 },
  emptySub: { fontSize: 13, color: '#BBB', textAlign: 'center' },

  grid: {
    flexDirection: 'row', flexWrap: 'wrap',
    paddingHorizontal: 20, paddingTop: 10, gap: 18,
    justifyContent: 'flex-start',
  },
  card: { width: 88, alignItems: 'center', position: 'relative' },
  avatarWrapper: { marginBottom: 8 },
  avatar: {
    width: 72, height: 72, borderRadius: 36,
    borderWidth: 2, borderColor: PURPLE,
  },
  avatarLoading: { justifyContent: 'center', alignItems: 'center', backgroundColor: '#F0E6FF' },
  avatarFallback: { backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center' },
  avatarFallbackText: { color: '#fff', fontSize: 22, fontWeight: 'bold' },
  cardName: { fontSize: 13, fontWeight: '600', color: '#333', textAlign: 'center' },
  removeBtn: {
    position: 'absolute', top: -4, right: 4,
    backgroundColor: '#fff', borderRadius: 10,
  },

  footer: {
    paddingHorizontal: 24, paddingBottom: Platform.OS === 'ios' ? 40 : 24,
    paddingTop: 16, gap: 12,
    borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: '#EEE',
  },
  otherAccountBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    borderWidth: 1.5, borderColor: PURPLE, borderRadius: 14, paddingVertical: 14,
  },
  otherAccountText: { fontSize: 15, fontWeight: '700', color: PURPLE },
  clearAllBtn: { alignItems: 'center', paddingVertical: 8 },
  clearAllText: { fontSize: 13, color: '#AAA', fontWeight: '600' },
});
